import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { FOUNDER, COMPANY } from "@/lib/siteData";

export default function Founder() {
    return (
        <section id="founder" data-testid="founder" className="relative py-24 md:py-32 bg-secondary/40">
            <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                {/* Image */}
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="lg:col-span-5 relative"
                >
                    <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border hairline">
                        <img
                            src={FOUNDER.image}
                            alt={`${FOUNDER.name} — ${FOUNDER.title}, CHANDAK BuildVision`}
                            data-testid="founder-image"
                            className="absolute inset-0 h-full w-full object-cover"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    </div>

                    {/* Floating name plaque */}
                    <div className="absolute -bottom-6 left-6 right-10 md:left-10 md:right-16 glass rounded-xl p-4 md:p-5 shadow-lg">
                        <div className="flex items-center gap-4">
                            <div className="h-10 w-1 bg-gold rounded-full" />
                            <div>
                                <div className="font-display text-lg md:text-xl tracking-tight">
                                    {FOUNDER.name}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                                    {FOUNDER.title} · CHANDAK BuildVision
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Content */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="lg:col-span-7 lg:pl-6"
                >
                    <div className="flex items-center gap-3 mb-5">
                        <span className="divider-gold" />
                        <span className="uppercase tracking-[0.28em] text-xs text-gold font-medium">
                            Meet the Founder
                        </span>
                    </div>

                    <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight">
                        A vision built on <span className="italic text-gold">craft</span>,
                        led with conviction.
                    </h2>

                    <p className="mt-6 text-lg leading-relaxed text-foreground/80 max-w-xl">
                        {FOUNDER.bio}
                    </p>

                    <div className="mt-8 relative p-6 md:p-8 rounded-2xl border hairline bg-card">
                        <Quote className="absolute -top-3 -left-3 h-8 w-8 text-gold bg-background rounded-full p-1.5 border hairline" />
                        <p className="font-display text-xl md:text-2xl italic leading-snug">
                            “{FOUNDER.ethos}”
                        </p>
                        <div className="mt-4 flex items-center gap-3">
                            <span className="divider-gold" />
                            <span className="text-xs uppercase tracking-widest text-muted-foreground">
                                {FOUNDER.name} · {FOUNDER.title}
                            </span>
                        </div>
                    </div>

                    <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
                        <div>
                            <div className="font-display text-3xl text-gold">10k+</div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                                Design Hours
                            </div>
                        </div>
                        <div>
                            <div className="font-display text-3xl text-gold">100+</div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                                Projects
                            </div>
                        </div>
                        <div>
                            <div className="font-display text-3xl text-gold">1</div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                                Promise Kept
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
